import { BlurText } from "@/components/common/BlurText";
import { ConnectionLost } from "@/components/common/ConnectionLost";
import { CourseCard } from "@/components/course/CourseCard";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useEnrollments } from "@/hooks/useEnrollments";
import { getCourses } from "@/lib/api";
import { NetworkError } from "@/lib/api-types";
import { queryKeys } from "@/lib/query-keys";
import { cn } from "@/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, BookOpen } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

type LanguageFilter = "all" | "en" | "pt";

export function Courses() {
	const { t } = useTranslation();
	const [langFilter, setLangFilter] = useState<LanguageFilter>("all");
	const { data: enrollments } = useEnrollments();

	const { data, isLoading, isError, error } = useQuery({
		queryKey: [...queryKeys.courses.all, langFilter],
		queryFn: () => getCourses(langFilter === "all" ? undefined : langFilter),
	});

	const courses = data?.courses ?? [];
	const enrollmentMap = new Map(enrollments?.map((e) => [e.course_id, e]) ?? []);

	const filters: { value: LanguageFilter; label: string }[] = [
		{ value: "all", label: t("courses.filterAll") },
		{ value: "en", label: "EN" },
		{ value: "pt", label: "PT" },
	];

	return (
		<div className="mx-auto max-w-7xl px-4 py-8 lg:px-8">
			<div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
				<BlurText text={t("nav.courses")} className="text-3xl font-bold text-primary" />

				<div
					className="flex gap-1 rounded-lg border border-white/10 bg-bg-elevated p-1"
					aria-label="Language filter"
				>
					{filters.map((f) => (
						<Button
							key={f.value}
							size="sm"
							variant="ghost"
							onClick={() => setLangFilter(f.value)}
							aria-pressed={langFilter === f.value}
							className={cn(
								"px-3 py-1 text-xs",
								langFilter === f.value
									? "bg-accent text-white hover:bg-accent-hover"
									: "text-secondary hover:text-primary",
							)}
						>
							{f.label}
						</Button>
					))}
				</div>
			</div>

			{isLoading ? (
				<div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" aria-busy="true">
					{Array.from({ length: 6 }).map((_, i) => (
						<Skeleton key={`skel-${i}`} className="h-64 rounded-xl" />
					))}
				</div>
			) : isError && error instanceof NetworkError ? (
				<ConnectionLost />
			) : isError ? (
				<div
					className="flex flex-col items-center gap-4 rounded-xl border border-white/10 bg-bg-elevated p-12 text-center"
					role="alert"
				>
					<AlertTriangle className="h-10 w-10 text-error" aria-hidden="true" />
					<h2 className="text-lg font-semibold text-primary">{t("error.loadFailed")}</h2>
					<p className="text-sm text-secondary">{t("error.loadFailedDesc")}</p>
				</div>
			) : courses.length === 0 ? (
				<div className="flex flex-col items-center gap-4 rounded-xl border border-white/10 bg-bg-elevated p-12 text-center">
					<BookOpen className="h-10 w-10 text-muted" aria-hidden="true" />
					<h2 className="text-lg font-semibold text-primary">{t("courses.noCourses")}</h2>
					<p className="text-sm text-secondary">{t("courses.noCoursesDesc")}</p>
				</div>
			) : (
				<div className="stagger-list grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
					{courses.map((course) => (
						<CourseCard key={course.id} course={course} enrollment={enrollmentMap.get(course.id)} />
					))}
				</div>
			)}
		</div>
	);
}
